import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ExclamationSpeechIcon } from '../assets/icons/ExclamationSpeech';
import { gaEvent, gaPageView } from '../services/ga/ga';
import { store } from '../store/store';
import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { getSystemCode } from '../utils/getSystemCode';
import { capturePostHogEvent } from '../services/posthog/capture';
import { enhanceSpan } from '../utils/otel/enhanceTrace';
import { useOTEL } from '../hooks/useOTEL';
import { useNavigate } from 'react-router-dom';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useConfirmation } from '../store/selectors/useConfirmation';
import { useGaEventData } from '../store/selectors/useGaEventData';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { StoreEvent } from '../enums/storeEvent';

export function ErrorPage() {
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const formData = useApplianceFormData();
  const confirmation = useConfirmation();
  const gaEventData = useGaEventData();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const { addEvent } = useOTEL();

  useEffect(() => {
    addEvent('ErrorPageViewed');
    enhanceSpan(
      'olr',
      {
        systemCode,
        sessionId: session?.id ?? 'unknown',
      },
      true,
    );

    gaPageView({
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });
  }, []);

  const handlePhoneNumber = () => {
    addEvent('ErrorPagePhoneNumberClicked');

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Phone Number clicked',
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided', // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-phone-clicked', store.get());
  };

  const handleTryAgain = () => {
    addEvent('ErrorPageTryAgainClicked');

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Generic Error page',
      eventLabel: 'Try Again button clicked',
      pagename: 'generic-error',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appliancePurchaseCost: formData.purchasePrice?.purchasePrice,
      appliancePurchaseDate: formData.purchaseDate?.purchaseDate,
      modelNumberDetail: formData.modelSerial?.modelNumber,
      serialNumberDetail: formData.modelSerial?.serialNumber,
      serialNumber: formData.modelSerial?.serialNumber ? 'provided' : 'not provided',
      // report "provided" if user submitted the serial number of the appliance, report "not provided" if they do not
      modelNumber: formData.modelSerial?.modelNumber ? 'provided' : 'not provided',
      offerPrice,
      planNumber: confirmation?.purchaseData?.planNumber,
    });

    capturePostHogEvent(posthog, 'error-try-again-clicked', store.get());
    store.send({ type: StoreEvent.INITIALISE });
    navigate('/', { replace: true });
  };

  return (
    <div className="grid place-items-center gap-4 mx-auto w-11/12 md:w-9/12 py-10 ">
      <ExclamationSpeechIcon />
      <h1 className="text-3xl font-semibold text-center">Sorry, something's not right.</h1>
      <p className="text-center">
        It looks like there's been a technical issue but we're working to fix it.
      </p>
      <p className="text-center">
        <strong>Please try again later or call us to complete your registration:</strong>
      </p>
      <TextLink onClick={handlePhoneNumber} href="tel:+1-833-552-2314">
        +1 833-552-2314
      </TextLink>
      <div className="w-full md:max-w-[360px]">
        <Button variant="primary" onClick={handleTryAgain}>
          Try Again
        </Button>
      </div>
    </div>
  );
}
